import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarUI } from "@/components/ui/calendar";
import { Plus, CalendarPlus, Download, MapPin, Clock, Trash2, Bell, BellOff, RefreshCw, Link2, Unlink, CheckCircle2, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { requestNotificationPermission, hasPermission, scheduleAll } from "@/lib/notifications";

const empty = {
  titolo: "", descrizione: "", data_ora: "", durata_minuti: 60,
  luogo: "", cantiere_id: "", tipo: "sopralluogo", partecipanti: [],
};

const tipoColor = {
  sopralluogo: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  riunione: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  consegna: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  altro: "bg-secondary text-secondary-foreground",
};

function googleCalLink(a) {
  const start = new Date(a.data_ora);
  const end = new Date(start.getTime() + (a.durata_minuti || 60) * 60000);
  const fmt = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
  const params = new URLSearchParams({
    action: "TEMPLATE", text: a.titolo || "",
    dates: `${fmt(start)}/${fmt(end)}`,
    details: a.descrizione || "", location: a.luogo || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export default function Appuntamenti() {
  const [list, setList] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [partText, setPartText] = useState("");
  const [notifOn, setNotifOn] = useState(false);
  // Calendar widget
  const [selectedDay, setSelectedDay] = useState(new Date());
  // Google Calendar status
  const [gcal, setGcal] = useState({ configured: false, connected: false, email: "", last_sync: "" });
  const [gcalBusy, setGcalBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "capocantiere";

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/appuntamenti"), api.get("/cantieri")]);
    setList(a.data); setCantieri(c.data);
    if (hasPermission()) scheduleAll(a.data.filter((x) => new Date(x.data_ora) > new Date()), 60);
  };

  const loadGcalStatus = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/status");
      setGcal(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    setNotifOn(hasPermission());
    load();
    loadGcalStatus();
  }, []);

  // Toast after redirect from Google OAuth
  const gcalParam = searchParams.get("gcal");
  useEffect(() => {
    if (gcalParam === "connected") {
      toast.success("Google Calendar collegato correttamente!");
      loadGcalStatus();
      searchParams.delete("gcal");
      setSearchParams(searchParams, { replace: true });
    } else if (gcalParam === "error") {
      const msg = searchParams.get("msg") || "sconosciuto";
      toast.error(`Errore Google Calendar: ${msg}`, { duration: 15000, description: "Guarda l'URL per il codice errore completo e inviamelo in chat." });
      // Non rimuovere il msg: aiuta a diagnosticare
      searchParams.delete("gcal");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [gcalParam]);

  const enableNotifs = async () => {
    const ok = await requestNotificationPermission();
    setNotifOn(ok);
    if (ok) { toast.success("Notifiche attive — ti avviseremo 1 ora prima"); scheduleAll(list.filter((x) => new Date(x.data_ora) > new Date()), 60); }
    else toast.error("Permesso negato — abilita le notifiche nelle impostazioni del browser");
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        durata_minuti: Number(form.durata_minuti),
        cantiere_id: form.cantiere_id || null,
        partecipanti: partText.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (editId) {
        await api.put(`/appuntamenti/${editId}`, payload);
        toast.success(gcal.connected && form.google_event_id ? "Modifiche aggiornate anche su Google Calendar" : "Appuntamento aggiornato");
      } else {
        await api.post("/appuntamenti", payload);
        toast.success(gcal.connected ? "Salvato — clicca 'Sincronizza' per inviarlo su Google Calendar" : "Appuntamento salvato");
      }
      setForm(empty); setEditId(null); setPartText(""); setOpen(false); load();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const openCreate = () => {
    setEditId(null);
    setForm(empty);
    setPartText("");
    setOpen(true);
  };

  const openEdit = (a) => {
    if (!canEdit) return;
    setEditId(a.id);
    // datetime-local vuole formato YYYY-MM-DDTHH:mm (local)
    const d = new Date(a.data_ora);
    const pad = (n) => String(n).padStart(2, "0");
    const localVal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setForm({
      titolo: a.titolo || "",
      descrizione: a.descrizione || "",
      data_ora: localVal,
      durata_minuti: a.durata_minuti || 60,
      luogo: a.luogo || "",
      cantiere_id: a.cantiere_id || "",
      tipo: a.tipo || "sopralluogo",
      partecipanti: a.partecipanti || [],
      google_event_id: a.google_event_id || "",
    });
    setPartText((a.partecipanti || []).join(", "));
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare l'appuntamento? Se collegato a Google Calendar verrà rimosso anche da lì.")) return;
    try { await api.delete(`/appuntamenti/${id}`); toast.success("Eliminato"); load(); } catch { toast.error("Errore"); }
  };

  // Google Calendar actions
  const connectGcal = async () => {
    setGcalBusy(true);
    try {
      const { data } = await api.get("/oauth/calendar/connect");
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error("Impossibile avviare il collegamento Google Calendar");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Google Calendar non configurato");
      setGcalBusy(false);
    }
  };

  const disconnectGcal = async () => {
    if (!window.confirm("Scollegare Google Calendar? I token verranno rimossi — gli appuntamenti locali restano.")) return;
    try {
      await api.post("/oauth/calendar/disconnect");
      toast.success("Google Calendar scollegato");
      loadGcalStatus();
    } catch { toast.error("Errore"); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/calendar/sync");
      toast.success(`Sync completata: ${data.pulled} nuovi · ${data.updated} aggiornati · ${data.pushed} pubblicati`);
      await load();
      await loadGcalStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la sincronizzazione");
    } finally {
      setSyncing(false);
    }
  };

  const cmap = Object.fromEntries(cantieri.map((c) => [c.id, c.nome]));
  const now = new Date();
  const futuri = list.filter((a) => new Date(a.data_ora) >= now);
  const passati = list.filter((a) => new Date(a.data_ora) < now);

  // Giorni che hanno almeno un appuntamento (per i pallini nel calendario)
  const daysWithEvents = useMemo(() => {
    const set = new Set();
    const arr = [];
    for (const a of list) {
      const d = new Date(a.data_ora);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!set.has(key)) {
        set.add(key);
        arr.push(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
      }
    }
    return arr;
  }, [list]);

  const eventsForSelectedDay = useMemo(
    () => list.filter((a) => sameDay(new Date(a.data_ora), selectedDay)).sort((a, b) => new Date(a.data_ora) - new Date(b.data_ora)),
    [list, selectedDay]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Agenda</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Appuntamenti & Sopralluoghi</h1>
          <p className="text-muted-foreground mt-1">Pianifica incontri in cantiere. Sincronizza con Google Calendar per averli ovunque.</p>
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            {/* Google Calendar status / actions */}
            {gcal.configured && !gcal.connected && (
              <Button
                variant="outline"
                className="h-11 border-primary/40 text-primary hover:bg-primary/10"
                onClick={connectGcal}
                disabled={gcalBusy}
                data-testid="gcal-connect"
              >
                {gcalBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                Connetti Google Calendar
              </Button>
            )}
            {gcal.connected && (
              <>
                <Button
                  variant="outline"
                  className="h-11 border-emerald-500/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"
                  onClick={syncNow}
                  disabled={syncing}
                  data-testid="gcal-sync"
                >
                  {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Sincronizza ora
                </Button>
                <Button
                  variant="ghost"
                  className="h-11 text-muted-foreground hover:text-destructive"
                  onClick={disconnectGcal}
                  data-testid="gcal-disconnect"
                  title={`Collegato come ${gcal.email}`}
                >
                  <Unlink className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button variant={notifOn ? "outline" : "default"} className="h-11" onClick={enableNotifs} data-testid="notif-toggle">
              {notifOn ? <Bell className="h-4 w-4 mr-2 text-emerald-600" /> : <BellOff className="h-4 w-4 mr-2" />}
              {notifOn ? "Notifiche attive" : "Attiva promemoria"}
            </Button>
            <Button data-testid="new-appuntamento" className="h-11" onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> Nuovo appuntamento</Button>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setForm(empty); setPartText(""); } }}>
              <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle>{editId ? "Modifica appuntamento" : "Nuovo appuntamento"}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Titolo</Label><Input data-testid="a-titolo" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} /></div>
                  <div>
                    <Label>Tipo</Label>
                    <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                      <SelectTrigger data-testid="a-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sopralluogo">Sopralluogo</SelectItem>
                        <SelectItem value="riunione">Riunione</SelectItem>
                        <SelectItem value="consegna">Consegna</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Cantiere</Label>
                    <Select value={form.cantiere_id || "__none__"} onValueChange={(v) => setForm({ ...form, cantiere_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Nessuno</SelectItem>
                        {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Data e ora</Label><Input data-testid="a-data" type="datetime-local" value={form.data_ora} onChange={(e) => setForm({ ...form, data_ora: e.target.value })} /></div>
                  <div><Label>Durata (minuti)</Label><Input type="number" value={form.durata_minuti} onChange={(e) => setForm({ ...form, durata_minuti: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Luogo</Label><Input value={form.luogo} onChange={(e) => setForm({ ...form, luogo: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Partecipanti (email separate da virgola, verranno invitate da Google Calendar)</Label><Input value={partText} onChange={(e) => setPartText(e.target.value)} placeholder="mario@acme.it, geom.verdi@..." /></div>
                  <div className="col-span-2"><Label>Descrizione</Label><Textarea rows={3} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
                </div>
                <Button data-testid="a-save" onClick={save} className="h-11 mt-2">{editId ? "Aggiorna" : "Salva"}</Button>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Riga di stato Google Calendar */}
      {gcal.connected && (
        <div className="flex items-center gap-2 text-xs rounded-xl bg-emerald-500/5 border border-emerald-500/20 px-4 py-2 w-fit" data-testid="gcal-status-bar">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <span className="font-semibold">Google Calendar collegato</span>
          <span className="text-muted-foreground">· {gcal.email}</span>
          {gcal.last_sync && <span className="text-muted-foreground">· Ultima sync: {new Date(gcal.last_sync).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>}
        </div>
      )}

      {/* Griglia 2 colonne: calendario + lista giorno selezionato */}
      <div className="grid lg:grid-cols-[auto,1fr] gap-6">
        <Card className="card-elevated w-fit">
          <CardContent className="p-2">
            <CalendarUI
              mode="single"
              selected={selectedDay}
              onSelect={(d) => d && setSelectedDay(d)}
              modifiers={{ hasEvent: daysWithEvents }}
              modifiersClassNames={{
                hasEvent: "relative font-bold text-primary after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-primary",
              }}
              className="rdp-cantiere"
              data-testid="agenda-calendar"
            />
          </CardContent>
        </Card>

        <Card className="card-elevated flex-1">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {selectedDay.toLocaleDateString("it-IT", { weekday: "long" })}
                </div>
                <h2 className="text-2xl font-extrabold">
                  {selectedDay.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
                </h2>
              </div>
              <Badge variant="outline" className="text-[11px]">{eventsForSelectedDay.length} eventi</Badge>
            </div>
            {eventsForSelectedDay.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Nessun appuntamento in questo giorno.
                {canEdit && <> Clicca "Nuovo appuntamento" per aggiungerne uno.</>}
              </div>
            ) : (
              <div className="space-y-2" data-testid="agenda-day-list">
                {eventsForSelectedDay.map((a) => (
                  <div
                    key={a.id}
                    className={`rounded-xl border border-border p-3 hover:bg-secondary/30 transition-colors ${canEdit ? "cursor-pointer" : ""}`}
                    onClick={canEdit ? () => openEdit(a) : undefined}
                    data-testid={`day-event-${a.id}`}
                    title={canEdit ? "Clicca per modificare" : undefined}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`badge-tag ${tipoColor[a.tipo]}`}>{a.tipo}</Badge>
                          <div className="font-bold">{a.titolo}</div>
                          {a.google_event_id && <Badge variant="outline" className="text-[9px] border-emerald-500/40 text-emerald-700"><CheckCircle2 className="h-2 w-2 mr-1" />GCal</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(a.data_ora).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · {a.durata_minuti}min
                        </div>
                        {a.luogo && <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{a.luogo}</div>}
                        {a.cantiere_id && <div className="text-xs text-primary mt-1">📍 {cmap[a.cantiere_id] || "Cantiere"}</div>}
                        {a.descrizione && <div className="text-xs text-foreground mt-1">{a.descrizione}</div>}
                      </div>
                      {canEdit && (
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(a); }} data-testid={`edit-${a.id}`} title="Modifica"><Pencil className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(a.id); }} title="Elimina"><Trash2 className="h-3 w-3 text-destructive" /></Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Section title={`Prossimi (${futuri.length})`} items={futuri} cmap={cmap} canEdit={canEdit} onDelete={remove} onEdit={openEdit} />
      <Section title={`Passati (${passati.length})`} items={passati} cmap={cmap} canEdit={canEdit} onDelete={remove} onEdit={openEdit} muted />
    </div>
  );
}

function Section({ title, items, cmap, canEdit, onDelete, onEdit, muted }) {
  if (items.length === 0) return null;
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{title}</div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((a) => {
          const dt = new Date(a.data_ora);
          const gl = googleCalLink(a);
          return (
            <Card
              key={a.id}
              className={`card-elevated ${muted ? "opacity-70" : ""} ${canEdit ? "cursor-pointer hover:border-primary/40 transition-colors" : ""}`}
              onClick={canEdit ? () => onEdit(a) : undefined}
              data-testid={`card-app-${a.id}`}
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`badge-tag ${tipoColor[a.tipo]}`}>{a.tipo}</Badge>
                      <div className="font-bold">{a.titolo}</div>
                      {a.google_event_id && <Badge variant="outline" className="text-[9px] border-emerald-500/40 text-emerald-700"><CheckCircle2 className="h-2 w-2 mr-1" />GCal</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock className="h-3 w-3" />
                      {dt.toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })} · {a.durata_minuti}min
                    </div>
                    {a.luogo && <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{a.luogo}</div>}
                    {a.cantiere_id && <div className="text-xs text-primary">📍 {cmap[a.cantiere_id] || "Cantiere"}</div>}
                    {a.descrizione && <div className="text-xs text-foreground mt-1">{a.descrizione}</div>}
                    {a.partecipanti?.length > 0 && <div className="flex gap-1 flex-wrap mt-1">{a.partecipanti.map((p, i) => <Badge key={i} variant="outline" className="text-[10px]">{p}</Badge>)}</div>}
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEdit(a); }} data-testid={`sec-edit-${a.id}`} title="Modifica"><Pencil className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onDelete(a.id); }} title="Elimina"><Trash2 className="h-3 w-3 text-destructive" /></Button>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-2 border-t border-border" onClick={(e) => e.stopPropagation()}>
                  <a href={gl} target="_blank" rel="noreferrer" className="flex-1" data-testid={`gcal-${a.id}`}>
                    <Button variant="outline" size="sm" className="w-full">
                      <CalendarPlus className="h-3 w-3 mr-1" /> Google Calendar
                    </Button>
                  </a>
                  <a href={`${API}/appuntamenti/${a.id}/ics`} className="flex-1" data-testid={`ics-${a.id}`}>
                    <Button variant="outline" size="sm" className="w-full">
                      <Download className="h-3 w-3 mr-1" /> .ics (iPhone/altro)
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
